import React, { useEffect, useState } from 'react'
import { GetServerSideProps } from 'next'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { useIsQueryOwner, useLocalStorage } from '@/hooks'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import useScrollPosition from '@/hooks/useScrollPosition'
import { useNotifications } from '@/modules/Notifications'
import useDisplayDistance from '../hooks/useDisplayDistance'
import { SEO, withPrivateRoute, withPrivateRouteProps } from '@/components'
import Footer from '@/components/EMT/Footer'
import { Navigation } from '@/components/EMT/Navigation'
import Map from '@/components/search/Map/Map'
import {
  EmtButton,
  EmtCard,
  EmtChip,
  EmtGridContainer,
  EmtIcon,
  EmtProgressBar,
  EmtTypography,
  OpenMapIcon,
  useMobile,
  useScrollBlock,
  useScrollTransform,
} from 'emt-components-v2'
import BorrowersNav from '@/components/BorrowersNav'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import Skeleton from '@mui/material/Skeleton'
import Backdrop from '@mui/material/Backdrop'
import FiltersButton from '@/components/search/FiltersButton'
import useFilters from '@/hooks/useFilters'
import useVisibleElement from '@/hooks/useVisibleElement'
import ProfileCardV2 from '@/components/EMT/ProfileCardV2'
import { IsThereAvatarBanner } from '@/components/IsThereAvatarBanner'
import { SearchServiceHelper } from '@/modules/Search'
import { SearchResults, SearchType } from '@/modules/Search/apiTypes/base'
import useCurrentUser from '@/hooks/useCurrentUser'
import FloatingProfileModal from '@/components/FloatingProfileModal'
import { InfoTextBox } from '@/components/PetSitter/ReservationCard/InfoTextBox'

type Props = withPrivateRouteProps & EMTServerSideProps

// Config
const maxProfilesPerPage = 78

const SearchPage: React.FC<Props> = ({ url }) => {
  const { t } = useTranslation('search')
  const { t: tc } = useTranslation('common')
  const { settings, hasRequestedPetsitting, isPremium } = useCurrentUser()
  const router = useRouter()
  const theme = useTheme()
  const [isMobile, isTablet] = useMobile()
  const [isQueryOwner, type] = useIsQueryOwner()
  const [addNotificationError] = useNotifications()
  const displayDistance = useDisplayDistance()
  const scrollPosition = useScrollPosition()
  const [blockScroll, allowScroll] = useScrollBlock()

  const { transformStyles } = useScrollTransform({
    translateY: {
      hidden: '124px',
      visible: '0',
    },
  })

  const [localStorageScrollPosition, setLocalStorageScrollPosition] = useLocalStorage('search_scrollPosition', 0)
  const [localStorageOpenMap, setLocalStorageOpenMap] = useLocalStorage('search_openmap', false)
  const [localStorageAddress, setLocalStorageAddress] = useLocalStorage('address', {
    lng: settings?.address?.longitude,
    lat: settings?.address?.latitude,
  })
  const [isFirstCall, setIsFirstCall] = useState<boolean>(true)
  const [fullRefetch, setFullRefetch] = useState<boolean>(false)

  const [footerIsVisible] = useVisibleElement('footer')

  // [HOOK] Filters
  const { filters, shortFilter, totalFilters, handleShortFilterClick, handleActiveFilter, FiltersComponent } =
    useFilters({
      onSearchClick: () => {
        setPage(1)
        setIsFirstCall(false)
        refetch().finally()
      },
      onShortFilterChange: () => {
        setPage(1)
        setIsFirstCall(false)
      },
      activeLocalStorage: true,
      activeHandleRouter: true,
    })

  // [HOOK] Floating Mobile Profile
  const floating = useFloatingProfile({
    defaultProfileKind: ProfileKind.Community,
  })

  // 🔥 API Calls
  const [page, setPage] = useState<number>(1)
  const isPageGreaterThanOne = page > 1

  // Search calls
  const queryKeys = [`profiles_${type}`]
  const { isLoading, data, refetch, isRefetching, isFetched } = useQuery<SearchResults>(
    queryKeys,
    async () => {
      try {
        const res = await SearchServiceHelper.searchProfilesAsync(
          isQueryOwner,
          {
            userAddress: settings?.address,
            filters,
            type: shortFilter,
            itemsPerPage: maxProfilesPerPage,
          },
          isPageGreaterThanOne ? dataNextPage : undefined,
        )

        return isPageGreaterThanOne
          ? ({
              member: [...dataProfiles, ...res.member],
              nextPage: res.nextPage,
              totalItems: res.totalItems,
            } satisfies SearchResults)
          : res
      } catch (err) {
        if (!isPageGreaterThanOne) {
          addNotificationError(err)
        }

        return { member: [], totalItems: 0 } satisfies SearchResults
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 300,
      enabled: false,
    },
  )

  const dataProfiles = data?.member || []
  const dataNextPage = data?.nextPage

  // Define if map is open or not
  const [activeMap, setActiveMap] = useState<boolean>(dataProfiles.length ? localStorageOpenMap : false)

  const handleActiveMap = () => {
    if (!activeMap) {
      blockScroll()
      setLocalStorageOpenMap(true)
    } else {
      document.ontouchmove = undefined
      allowScroll()
      setLocalStorageOpenMap(false)
    }
    setActiveMap((prev) => {
      return !prev
    })
  }

  const handleMoreProfilesClick = () => {
    setIsFirstCall(false)
    if (!dataNextPage) return
    setPage((prev) => prev + 1)
  }

  // ⚓ Scroll position behaviors
  useEffect(() => {
    // On mobile, only reset the scroll if NOT coming back from messages.
    // if (isMobile && router.query.from !== 'message') {
    //   setLocalStorageScrollPosition(0)
    //   return
    // }

    // If there is no stored scroll position, do nothing.
    if (!localStorageScrollPosition) return

    // Scroll only if it hasn’t been done already and there are profiles to scroll to.
    if (dataProfiles.length) {
      window.scrollTo({ top: localStorageScrollPosition })
    } else if (!dataProfiles.length) {
      setLocalStorageScrollPosition(0)
    }
  }, [router])

  // 🔥Refetch when address change
  useEffect(() => {
    if (
      settings?.address?.longitude !== localStorageAddress.lng ||
      settings?.address?.latitude !== localStorageAddress.lat
    ) {
      setFullRefetch(true)
      refetch().then(() => setFullRefetch(false))
    }
  }, [localStorageAddress])

  // 🔥Refetch when asking more profiles
  useEffect(() => {
    if (page === 1) return
    refetch()
  }, [page])

  // 🔥Refetch when shortFilter change
  useEffect(() => {
    if ((isFirstCall && isFetched) || !filters) return
    refetch()
  }, [shortFilter, filters, router.query['type']])

  useEffect(() => {
    if (!isFirstCall) return
    if (localStorageOpenMap) blockScroll()
  }, [localStorageOpenMap])

  useEffect(() => {
    // If user address has changed
    if (
      settings?.address?.longitude !== localStorageAddress.lng ||
      settings?.address?.latitude !== localStorageAddress.lat
    ) {
      setLocalStorageAddress({
        lng: settings?.address?.longitude,
        lat: settings?.address?.latitude,
      })
    }
    return () => {
      document.ontouchmove = undefined
      allowScroll()
    }
  }, [])

  return (
    <>
      <SEO url={`${url}`} subtitle={t(`title.${type}`) as string} />

      <Box sx={{ position: 'relative' }}>
        <Box sx={{ overflow: 'hidden' }}>
          <Navigation />
          {/* Nav borrowers */}
          {!isMobile && isQueryOwner && hasRequestedPetsitting && <BorrowersNav />}
          {/* END Nav borrowers */}
          <IsThereAvatarBanner t={tc} />
          <Container sx={{ paddingTop: '30px' }} maxWidth="lg">
            <EmtGridContainer columnSpacing={{ xs: 2, lg: 3 }} rowSpacing={{ xs: 2, lg: 3 }}>
              {/* Title & Cards & Short filter */}
              <Grid item xs={12}>
                <EmtGridContainer columnSpacing={{ xs: 2 }} rowSpacing={{ xs: 2 }}>
                  {/* Title & short filters & Map */}
                  <Grid item xs={12} sx={{ position: 'relative' }}>
                    {/* TITLE */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: isMobile ? 'inherit' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                      }}
                    >
                      <EmtTypography variant="h3" fontWeight="900" className="EmtReactNativeWebViewMarginTop">
                        {t(`title.${type}`)}
                      </EmtTypography>
                    </Box>
                    {/* END TITLE */}

                    {/* SHORT FILTER */}
                    <Box
                      sx={{
                        margin: isMobile || isTablet ? ' 0 -16px' : '0',
                        position: 'relative',
                        ...(isMobile && {
                          '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: '16px',
                            pointerEvents: 'none',
                            zIndex: 1,
                          },
                          '&::before': {
                            left: 0,
                            background: 'linear-gradient(-270deg, #F9F9F9 0%, rgba(249, 249, 249, 0) 100%)',
                          },
                          '&::after': {
                            right: 0,
                            background: 'linear-gradient(270deg, #F9F9F9 0%, rgba(249, 249, 249, 0) 100%)',
                          },
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '6px',
                          marginTop: isMobile || isTablet ? 0 : '15px',
                          overflow: 'auto',
                          '&::-webkit-scrollbar': { display: 'none' },
                          msOverflowStyle: 'none',
                          scrollbarWidth: 'none',
                          padding: isMobile || isTablet ? '20px 16px' : '0 0 20px',
                          position: 'relative',
                        }}
                      >
                        <EmtChip
                          label={t('shortFilters.recommand')}
                          color="primarywhite"
                          {...(shortFilter === SearchType.recommendation ? { className: 'active' } : {})}
                          icon={<EmtIcon className="emt-star-1" />}
                          onClick={() => handleShortFilterClick(SearchType.recommendation)}
                        />
                        {/* <EmtChip
                          label={t('shortFilters.closest')}
                          color="primarywhite"
                          {...(shortFilter === SearchType.distance ? { className: 'active' } : {})}
                          icon={<EmtIcon className="emt-pin-1" />}
                          onClick={() => handleShortFilterClick(SearchType.distance)}
                        /> */}
                        <EmtChip
                          label={t('shortFilters.createdAt')}
                          color="primarywhite"
                          {...(shortFilter === SearchType.createdAt ? { className: 'active' } : {})}
                          icon={<EmtIcon className="emt-clock-1" />}
                          onClick={() => handleShortFilterClick(SearchType.createdAt)}
                        />

                        {/* 🍫TABLET MAP & FILTER BUTTON */}
                        {isTablet && !isMobile ? (
                          <Box
                            sx={{
                              display: 'flex',
                              position: 'absolute',
                              right: totalFilters && '8px',
                              bottom: '15px',
                              gap: '10px',
                            }}
                          >
                            <EmtButton
                              color="primary"
                              startIcon={<OpenMapIcon />}
                              fullWidth={false}
                              sx={{ alignItems: 'center', fontWeight: '600' }}
                              onClick={handleActiveMap}
                              className="mapopenga"
                            >
                              <EmtTypography variant="body2">{t('map.buttons.seeMap')}</EmtTypography>
                            </EmtButton>

                            <FiltersButton onClick={handleActiveFilter} count={filters ? totalFilters : undefined} />
                          </Box>
                        ) : null}
                        {/* 🍫END TABLET MAP & FILTER BUTTON */}
                      </Box>
                    </Box>
                    {/* END SHORT FILTER */}

                    {/* INFO TEXT BOX */}
                    {isPremium && (
                      <InfoTextBox
                        closable
                        closeDuration={5 * 24 * 60 * 60 * 1000}
                        cookieKey="search-info-box"
                        infoText={
                          <EmtTypography variant="body2">
                            <Trans
                              t={t}
                              i18nKey={'infoTextBox'}
                              components={{
                                IconCrown: (
                                  <EmtIcon className="emt-crown" sx={{ fontSize: '14px', marginRight: '4px' }} />
                                ),
                                strong: <strong />,
                                Inseparables: (
                                  <span
                                    style={{
                                      whiteSpace: 'nowrap',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      color: '#B89606',
                                      fontWeight: '700',
                                      lineHeight: '0',
                                      height: '0px',
                                      transform: 'translateY(2px)',
                                      marginLeft: '4px',
                                    }}
                                  />
                                ),
                              }}
                            />
                          </EmtTypography>
                        }
                        sx={{ border: `1px solid #EAEAEA` }}
                      />
                    )}
                    {/* END INFO TEXT BOX */}

                    {/* 🖥️ TOTAL MEMBERS */}
                    {!isMobile && !isTablet ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '28px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          rowGap: '5px',
                        }}
                      >
                        {data && filters && !isRefetching ? (
                          <>
                            <EmtTypography variant="h5" color="greyGradient.main">
                              {t(`finding.${type}`, { count: data.totalItems })}{' '}
                              <EmtTypography variant="caption" fontWeight={500} fontSize={16}>
                                ({displayDistance(Number(filters.distance))})
                              </EmtTypography>
                            </EmtTypography>
                          </>
                        ) : (
                          <Skeleton variant="text" width={270} height={30} />
                        )}

                        {/* MAP & FILTER BUTTON */}
                        <Box sx={{ display: 'flex', gap: '10px' }}>
                          <EmtButton
                            color="primary"
                            startIcon={<OpenMapIcon />}
                            fullWidth={false}
                            sx={{ alignItems: 'center', fontWeight: '600' }}
                            onClick={handleActiveMap}
                          >
                            <EmtTypography variant="body2">{t('map.buttons.seeMap')}</EmtTypography>
                          </EmtButton>
                          <FiltersButton onClick={handleActiveFilter} count={filters ? totalFilters : undefined} />
                        </Box>
                        {/* END MAP & FILTER BUTTON */}
                      </Box>
                    ) : null}
                    {/* 🖥️ END TOTAL MEMBERS */}

                    {isMobile || isTablet ? (
                      <>
                        {data && filters && !isRefetching ? (
                          <EmtTypography variant="body1" fontWeight="bold" sx={{ marginTop: '16px' }}>
                            {t(`finding.${type}`, { count: data.totalItems })}{' '}
                            <EmtTypography variant="caption" fontWeight={500} fontSize={16}>
                              ({displayDistance(Number(filters.distance))})
                            </EmtTypography>
                          </EmtTypography>
                        ) : (
                          <Skeleton variant="text" width={270} height={30} />
                        )}
                      </>
                    ) : null}
                    {/* END TOTAL MEMBERS */}
                  </Grid>

                  {/* PROFILES */}
                  {(isLoading && !dataProfiles.length) || (isRefetching && !isFirstCall && page === 1) || fullRefetch
                    ? [...Array(8)].map((v, k) => (
                        <Grid key={k} item xs={6} md={3}>
                          <EmtCard loader />
                        </Grid>
                      ))
                    : dataProfiles.map((profile, k) => (
                        <Grid
                          key={k}
                          item
                          xs={6}
                          md={3}
                          sx={{
                            transition: theme.transitions.create('all', {
                              easing: theme.transitions.easing.sharp,
                              duration: theme.transitions.duration.leavingScreen,
                            }),
                          }}
                        >
                          <div onClickCapture={() => setLocalStorageScrollPosition(scrollPosition)}>
                            <ProfileCardV2
                              profile={profile}
                              onMobileClickCard={floating.handleOpenProfile}
                              contactQuery={{ from: 'search' }}
                            />
                          </div>
                        </Grid>
                      ))}

                  {isRefetching &&
                    dataNextPage &&
                    [...Array(8)].map((v, k) => (
                      <Grid key={k} item xs={6} md={3}>
                        <EmtCard loader />
                      </Grid>
                    ))}
                  {/* END PROFILES */}

                  {/* LOAD MORE BUTTON */}
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      marginTop: '15px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      rowGap: '8px',
                    }}
                  >
                    {dataNextPage && isFetched ? (
                      <>
                        {isLoading || isRefetching ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Skeleton variant="text" height={15} width={250} />
                            <Skeleton variant="text" height={10} width={230} />
                          </Box>
                        ) : (
                          <>
                            <EmtTypography variant="body2">
                              {t(`moreProfiles.${type}.title`, { count: dataProfiles.length, total: data.totalItems })}
                            </EmtTypography>
                            <EmtProgressBar value={(dataProfiles.length / data.totalItems) * 100} />
                          </>
                        )}

                        <EmtButton
                          loader={isLoading || isRefetching}
                          sx={{ marginTop: '5px', ...(isMobile ? {} : { padding: '6px 40px' }) }}
                          fullWidth={isMobile}
                          onClick={handleMoreProfilesClick}
                        >
                          {t(`moreProfiles.${type}.button`)}
                        </EmtButton>
                      </>
                    ) : null}
                  </Grid>
                  {/* END LOAD MORE BUTTON */}
                </EmtGridContainer>
              </Grid>
              {/* END Title & Cards & Short filter */}
            </EmtGridContainer>
          </Container>
        </Box>

        {/* 📱MOBILE FILTERS BUTTON */}
        {isMobile ? (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              position: footerIsVisible ? 'absolute' : 'fixed',
              bottom: footerIsVisible ? `-200px` : '150px',
              right: '20px',
              zIndex: 1,
              ...transformStyles,
            }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <EmtButton
              color="primary"
              startIcon={<OpenMapIcon />}
              fullWidth={false}
              sx={{
                alignItems: 'center',
                fontWeight: '600',
                padding: '7px',
                '&.MuiButtonBase-root': {
                  flexDirection: 'column',
                  '& .MuiButton-startIcon': {
                    margin: '0',
                  },
                },
              }}
              onClick={handleActiveMap}
              className="mapopenga"
            >
              <EmtTypography variant="body2" sx={{ position: 'relative', top: '2px' }}>
                {t('map.buttons.seeMapMobile')}
              </EmtTypography>
            </EmtButton>
            <FiltersButton compact={true} onClick={handleActiveFilter} count={totalFilters} />
          </Box>
        ) : null}
        {/* 📱END MOBILE FILTERS BUTTON */}

        {/* FILTERS */}
        {!activeMap ? FiltersComponent() : null}
        {/* END FILTERS */}
      </Box>
      <Footer />

      {/* MOBILE PROFILE MODAL */}
      <FloatingProfileModal
        isMobile={floating.isMobile}
        open={floating.profileIsOpen}
        customerId={floating.customerId}
        profileKind={floating.profileKind}
        onClose={floating.handleCloseProfile}
      />
      {/* END MOBILE PROFILE MODAL */}

      {/* 🗺️ MAP */}
      {activeMap && !isMobile && (
        <>
          <Map onClose={handleActiveMap} itemsPerPage={50} />
          <Backdrop open={true} sx={{ zIndex: 10 }} onClick={handleActiveMap} />
        </>
      )}

      {activeMap && isMobile && (
        <>
          <Map onClose={handleActiveMap} itemsPerPage={50} floating={floating} />
          <Backdrop open={true} sx={{ zIndex: 10 }} onClick={handleActiveMap} />
        </>
      )}
      {/* 🗺️ END MAP */}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = getEMTServerSideProps({
  namespaces: ['common', 'search', 'profile', 'message', 'connection'],
  i18nTitleNs: 'search',
  i18nTitleKey: 'title.seo',
})

export default withPrivateRoute()(SearchPage)
